import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Avatar } from "../components/Layout";
import { EVENT_LABELS } from "../lib/bouquet";
import { BarChart3, Lock, Pencil, Plus, UsersRound, UserRound, Trash2 } from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";

function CommunityManager() {
  const [communities, setCommunities] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", city: "", description: "", lead_user_id: "" });
  const [memberEmail, setMemberEmail] = useState({});

  const load = () => { api.get("/admin/communities").then((r) => setCommunities(r.data)).catch(() => {}); };
  useEffect(load, []);

  const openEdit = (c) => {
    setEditId(editId === c.chapter_id ? null : c.chapter_id);
    setEditForm({ name: c.name, city: c.city || "", description: c.description || "", lead_user_id: c.lead_user_id || "" });
  };

  const save = async (c) => {
    try {
      await api.put(`/admin/communities/${c.chapter_id}`, editForm);
      toast.success("Community updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const addMember = async (c) => {
    const email = (memberEmail[c.chapter_id] || "").trim();
    if (!email) return;
    try {
      const res = await api.post(`/admin/communities/${c.chapter_id}/members`, { email });
      toast.success(`${res.data.name} added to ${c.name}`);
      setMemberEmail({ ...memberEmail, [c.chapter_id]: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add member");
    }
  };

  const removeMember = async (c, m) => {
    await api.delete(`/admin/communities/${c.chapter_id}/members/${m.user_id}`);
    toast.success(`${m.name} removed from ${c.name}`);
    load();
  };

  return (
    <section className="space-y-4" data-testid="community-manager">
      <div className="flex items-center gap-2">
        <UsersRound className="h-4 w-4 text-accent" />
        <p className="font-display text-lg font-medium">Communities</p>
      </div>
      <div className="space-y-3">
        {communities.map((c) => (
          <div key={c.chapter_id} className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid={`admin-community-${c.chapter_id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-sm">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.city || "—"} · {(c.members || []).length} members</p>
              </div>
              <button data-testid={`admin-community-edit-${c.chapter_id}`} onClick={() => openEdit(c)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary transition-colors shrink-0">
                <Pencil className="h-3 w-3" /> Edit
              </button>
            </div>

            {editId === c.chapter_id && (
              <div className="space-y-3 pt-2 border-t border-border" data-testid={`admin-community-form-${c.chapter_id}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input data-testid={`admin-community-name-${c.chapter_id}`} value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" className={inputCls} />
                  <input data-testid={`admin-community-city-${c.chapter_id}`} value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} placeholder="City" className={inputCls} />
                </div>
                <textarea data-testid={`admin-community-description-${c.chapter_id}`} value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={2} placeholder="Description" className={inputCls} />
                <div>
                  <p className="text-xs font-semibold mb-1.5">Community steward (lead)</p>
                  <select data-testid={`admin-community-lead-${c.chapter_id}`} value={editForm.lead_user_id}
                    onChange={(e) => setEditForm({ ...editForm, lead_user_id: e.target.value })} className={inputCls}>
                    <option value="">No steward</option>
                    {(c.members || []).map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                  </select>
                </div>
                <button data-testid={`admin-community-save-${c.chapter_id}`} onClick={() => save(c)}
                  className="bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-xs font-semibold hover:opacity-90 transition-opacity">Save community</button>
              </div>
            )}

            <div className="space-y-2">
              {(c.members || []).map((m) => (
                <div key={m.user_id} className="flex items-center gap-3" data-testid={`admin-member-${m.user_id}`}>
                  <Avatar user={m} size="h-7 w-7" />
                  <p className="text-xs font-medium flex-1 min-w-0 truncate">{m.name} <span className="text-muted-foreground font-normal">· {m.email}</span></p>
                  {c.lead_user_id === m.user_id && <span className="px-2 py-0.5 rounded-full bg-sage-pale text-[10px] font-semibold shrink-0">Steward</span>}
                  <button data-testid={`admin-member-remove-${m.user_id}`} onClick={() => removeMember(c, m)}
                    className="text-muted-foreground hover:text-destructive px-1 shrink-0">×</button>
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <input data-testid={`admin-member-email-${c.chapter_id}`} value={memberEmail[c.chapter_id] || ""}
                  onChange={(e) => setMemberEmail({ ...memberEmail, [c.chapter_id]: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && addMember(c)}
                  placeholder="Add member by email (must be on Bouquet)" className={inputCls} />
                <button data-testid={`admin-member-add-${c.chapter_id}`} onClick={() => addMember(c)}
                  className="px-3.5 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-secondary transition-colors shrink-0 flex items-center gap-1">
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProfileManager() {
  const [members, setMembers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", title: "", company: "", role: "member" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => { api.get("/admin/users").then((r) => setMembers(r.data)).catch(() => {}); };
  useEffect(load, []);

  const openEdit = (m) => {
    setEditId(editId === m.user_id ? null : m.user_id);
    setEditForm({ name: m.name || "", title: m.title || "", company: m.company || "", role: m.role || "member" });
  };

  const save = async (m) => {
    try {
      await api.put(`/admin/users/${m.user_id}`, editForm);
      toast.success("Profile updated");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const activate = async (m) => {
    try {
      await api.put(`/admin/users/${m.user_id}`, { onboarding_status: "active" });
      toast.success(`${m.name} activated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not activate");
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/admin/users/${m.user_id}`);
      toast.success(`${m.name} removed`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <section className="space-y-4" data-testid="profile-manager">
      <div className="flex items-center gap-2">
        <UserRound className="h-4 w-4 text-accent" />
        <p className="font-display text-lg font-medium">Member Profiles</p>
      </div>
      <div className="bg-card border border-border rounded-xl divide-y divide-border">
        {members.map((m) => (
          <div key={m.user_id} className="p-4 space-y-3" data-testid={`admin-profile-${m.user_id}`}>
            <div className="flex items-center gap-3">
              <Avatar user={m} size="h-8 w-8" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.name}{" "}
                  {m.role === "central_admin" && <span className="px-2 py-0.5 rounded-full bg-sage-pale text-[10px] font-semibold">Admin</span>}
                  {m.onboarding_status !== "active" && <span className="px-2 py-0.5 rounded-full bg-secondary text-[10px] font-semibold">Pending</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{m.email}{m.title ? ` · ${m.title}` : ""}</p>
              </div>
              {m.onboarding_status !== "active" && (
                <button data-testid={`admin-profile-activate-${m.user_id}`} onClick={() => activate(m)}
                  className="px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold hover:opacity-90 transition-opacity shrink-0">Activate</button>
              )}
              <button data-testid={`admin-profile-edit-${m.user_id}`} onClick={() => openEdit(m)}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors shrink-0"><Pencil className="h-3.5 w-3.5" /></button>
              {m.role !== "central_admin" && (
                confirmDelete === m.user_id ? (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`admin-profile-delete-confirm-${m.user_id}`} onClick={() => remove(m)}
                      className="px-3 py-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold">Delete</button>
                    <button data-testid={`admin-profile-delete-cancel-${m.user_id}`} onClick={() => setConfirmDelete(null)}
                      className="px-3 py-1.5 rounded-full border border-border text-[11px] font-semibold">Cancel</button>
                  </div>
                ) : (
                  <button data-testid={`admin-profile-delete-${m.user_id}`} onClick={() => setConfirmDelete(m.user_id)}
                    className="p-2 text-muted-foreground hover:text-destructive transition-colors shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                )
              )}
            </div>
            {editId === m.user_id && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border" data-testid={`admin-profile-form-${m.user_id}`}>
                <input data-testid={`admin-profile-name-${m.user_id}`} value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" className={inputCls} />
                <input data-testid={`admin-profile-title-${m.user_id}`} value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="How they describe what they do" className={inputCls} />
                <input data-testid={`admin-profile-company-${m.user_id}`} value={editForm.company}
                  onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} placeholder="Company" className={inputCls} />
                <select data-testid={`admin-profile-role-${m.user_id}`} value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className={inputCls}>
                  <option value="member">Member</option>
                  <option value="central_admin">Central Admin</option>
                </select>
                <button data-testid={`admin-profile-save-${m.user_id}`} onClick={() => save(m)}
                  className="sm:col-span-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-xs font-semibold hover:opacity-90 transition-opacity">Save profile</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    if (user.role === "central_admin") {
      api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    }
  }, [user.role]);

  if (user.role !== "central_admin") return <Navigate to="/" replace />;
  if (!metrics) return <div className="animate-pulse text-muted-foreground text-sm">Loading…</div>;

  const coreEvents = ["intention_created", "step_created", "step_shared", "support_response", "reflection_completed",
    "outcome_did_it", "outcome_made_progress", "outcome_changed_step", "outcome_got_stuck", "outcome_released",
    "help_requested", "moment_created", "gathering_created", "gathering_rsvp", "pod_created"];

  return (
    <div data-testid="admin-metrics" className="space-y-8">
      <PageHeader eyebrow="Private · Admin Only" title="Product Learning"
        description="Aggregate counts only — never per-person, never small-group breakdowns. This exists to learn whether the loop is working, not to score anyone." />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[["members", "Members"], ["pods", "Pods"], ["intentions", "Intentions"], ["moments", "Moments"], ["gatherings", "Gatherings"]].map(([key, label]) => (
          <div key={key} className="bg-card border border-border rounded-xl p-4" data-testid={`total-${key}`}>
            <p className="font-display text-2xl font-medium">{metrics.totals[key]}</p>
            <p className="text-[11px] text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl p-6" data-testid="core-loop-metrics">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-4 w-4 text-accent" />
          <p className="font-display text-lg font-medium">Core Loop Events</p>
        </div>
        <div className="divide-y divide-border">
          {coreEvents.map((key) => (
            <div key={key} className="flex items-center justify-between py-2.5" data-testid={`metric-${key}`}>
              <span className="text-sm">{EVENT_LABELS[key] || key}</span>
              <span className="font-mono2 text-sm font-semibold">{metrics.events[key] || 0}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> No private text, intentions, reflections, or individual histories are captured here.
        </p>
      </div>

      <CommunityManager />

      <ProfileManager />
    </div>
  );
}
